import styled from "@emotion/styled";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CheckBoxEmpty, CheckBoxFull } from "../../assets/icons/icons";
import { MemberDetail } from "../../types/types";
import { Auths } from "./userModifyModal";
import userStorage from "../../utils/userStorage";
import theme from "../../theme";
import { useI18n } from "../../locales";

const { sm, md, lg } = theme.screen;

const AuthField: React.FC<{
  data?: MemberDetail;
  selectedAuth?: Auths;
  setSelectedAuth: Dispatch<SetStateAction<Auths | undefined>>;
  isMyPage?: boolean;
}> = (props) => {
  const { data, selectedAuth, setSelectedAuth, isMyPage } = props;
  const { t } = useI18n();
  const [isAdmin, setIsAdmin] = useState(false);
  const user = userStorage.get();

  useEffect(() => {
    // 마이페이지에서 grade가 2인 경우만 수정 권한이 주어짐.
    if ((isMyPage && user?.grade === 2) || !isMyPage) {
      setIsAdmin(true);
    }
  }, [user, isMyPage]);

  const onClickBox = (auth: string) => {
    if (isMyPage && user?.grade !== 2) {
      return;
    } else {
      setSelectedAuth((prev) => {
        if (prev !== undefined && prev[auth] === "Y") {
          return { ...prev, [auth]: "N" };
        } else if (prev !== undefined && prev[auth] === "N") {
          return { ...prev, [auth]: "Y" };
        }
      });
    }
  };

  useEffect(() => {
    if (data) {
      // permission이 없을때 초기화
      if (!data.permission) {
        setSelectedAuth({
          auth_accident: "N",
          auth_battery: "N",
          auth_check: "N",
          auth_modify: "N",
          auth_notice: "N",
          auth_out: "N",
          auth_warning: "N",
        });
        return;
      }
      setSelectedAuth(data.permission);
      return;
    }
  }, [data, setSelectedAuth]);

  return (
    <AuthWrapper className="noselect">
      <FirstCol>
        <AuthHeader>{t.manageUser.detectionNotification}</AuthHeader>
        <CheckBoxWrapper>
          <SelectWrapper
            onClick={() => onClickBox("auth_accident")}
            isAdmin={isAdmin}
          >
            <CheckBoxIconWrapper isAdmin={isAdmin}>
              {selectedAuth?.auth_accident === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <SelectText>{t.manageUser.dangerAccident}</SelectText>
          </SelectWrapper>
          <SelectWrapper
            onClick={() => onClickBox("auth_warning")}
            isAdmin={isAdmin}
          >
            <CheckBoxIconWrapper isAdmin={isAdmin}>
              {selectedAuth?.auth_warning === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <SelectText>{t.manageUser.warningDangerousArea}</SelectText>
          </SelectWrapper>
          <SelectWrapper
            onClick={() => onClickBox("auth_battery")}
            isAdmin={isAdmin}
          >
            <CheckBoxIconWrapper isAdmin={isAdmin}>
              {selectedAuth?.auth_battery === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <SelectText>{t.manageUser.batteryAbnormal}</SelectText>
          </SelectWrapper>
          <SelectWrapper
            onClick={() => onClickBox("auth_out")}
            isAdmin={isAdmin}
          >
            <CheckBoxIconWrapper isAdmin={isAdmin}>
              {selectedAuth?.auth_out === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <SelectText>{t.manageUser.areaExit}</SelectText>
          </SelectWrapper>
        </CheckBoxWrapper>
      </FirstCol>
      <SecondCol>
        <AuthHeader>{t.manageUser.functionManagement}</AuthHeader>
        <CheckBoxWrapper>
          <SelectWrapper
            onClick={() => onClickBox("auth_check")}
            isAdmin={isAdmin}
          >
            <CheckBoxIconWrapper isAdmin={isAdmin}>
              {selectedAuth?.auth_check === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <SelectText>{t.manageUser.detectionRelease}</SelectText>
          </SelectWrapper>
          <SelectWrapper
            onClick={() => onClickBox("auth_modify")}
            isAdmin={isAdmin}
          >
            <CheckBoxIconWrapper isAdmin={isAdmin}>
              {selectedAuth?.auth_modify === "Y" ? (
                <CheckBoxFull />
              ) : (
                <CheckBoxEmpty />
              )}
            </CheckBoxIconWrapper>
            <SelectText>{t.manageUser.deviceInfoModify}</SelectText>
          </SelectWrapper>
        </CheckBoxWrapper>
      </SecondCol>
    </AuthWrapper>
  );
};

const AuthWrapper = styled.div`
  padding-top: 20px;
  padding-bottom: 30px;
  padding-left: 30px;
  background: ${theme.color.neutral_50};
  display: flex;
  margin-bottom: 40px;
  border-radius: 10px;

  @media (max-width: ${sm}px) {
    padding: 10px;
  }

  @media (max-width: ${lg}px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const FirstCol = styled.div`
  @media (min-width: ${lg + 1}px) {
    margin-right: 40px;
  }
`;

const SecondCol = styled.div`
  @media (min-width: ${lg + 1}px) {
    margin-right: 40px;
  }
`;

const AuthHeader = styled.p`
  font-weight: 400;
  font-size: 13px;
  line-height: 24px;
  color: ${theme.color.neutral_800};
  margin-bottom: 18px;
`;

const SelectWrapper = styled.div<{ isAdmin?: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  border-radius: 10px;
  cursor: ${({ isAdmin }) => (isAdmin ? "pointer" : "default")};
  &:hover {
    background-color: ${({ isAdmin }) =>
      isAdmin ? theme.color.neutral_100 : "none"};
  }
`;

const SelectText = styled.span`
  font-weight: 400;
  font-size: 13px;
  line-height: 15px;
  color: ${theme.color.neutral_600};

  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

const CheckBoxIconWrapper = styled.button<{ isAdmin?: boolean }>`
  transform: translateY(1px);
  cursor: ${({ isAdmin }) => (isAdmin ? "pointer" : "default")};
`;

const CheckBoxWrapper = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: ${md}px) {
    flex-direction: column;
    padding: 10px;
    gap: 20px;
  }
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    gap: 10px;
  }
`;

export default AuthField;
